import { Grid, Box, Typography } from '@mui/material';
import { TaskAlt, WarningAmber } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { CardContent } from '@occc/ui-design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';

import { RetailerNetworkMonthlyGoalCircularProgress } from './retailer-network-monthly-goal-circular-progress.component';
import {
  NarrativeTextParams,
  calculatePercentOfNumber,
} from './retailer-network-monthly-goals.utils';
import { RetailerNetworkMonthlyGoalProgressDescription } from './retailer-network-monthly-goal-progress-description.component';

import { isPerformanceOnPace } from '../../retailer-connect.utils';

type RetailerNetworkPerformanceProps = {
  newMembersGoalProgress: number;
  newMembersGoal: number;
  loyaltyTransactionsGoalProgress: number;
  loyaltyTransactionsGoal: number;
  homeStoreMembers: number;
  narrativeTextData: NarrativeTextParams;
};

export const RetailerNetworkMonthlyGoals = ({
  newMembersGoalProgress,
  newMembersGoal,
  loyaltyTransactionsGoalProgress,
  loyaltyTransactionsGoal,
  homeStoreMembers,
  narrativeTextData,
}: RetailerNetworkPerformanceProps) => {
  const theme = useTheme();

  const newMembersColor = isPerformanceOnPace(newMembersGoalProgress, newMembersGoal)
    ? theme.palette.success.main
    : theme.palette.error.main;
  const loyaltyTransactionsColor = isPerformanceOnPace(
    loyaltyTransactionsGoalProgress,
    loyaltyTransactionsGoal,
  )
    ? theme.palette.success.main
    : theme.palette.error.main;

  const narrativeBoxBgColor = theme.palette[narrativeTextData.narrativeTextBoxColorVariant].light;

  return (
    <CardContent>
      <Grid container justifyContent='center'>
        <Grid
          item
          display='flex'
          flexDirection='row'
          sx={{ marginBottom: { xs: 1, sm: 0 } }}
          xs={12}
          sm={4}
          pr={1}
          alignItems='center'
        >
          <RetailerNetworkMonthlyGoalCircularProgress
            color={newMembersColor}
            value={calculatePercentOfNumber(newMembersGoalProgress, newMembersGoal)}
          />
          <RetailerNetworkMonthlyGoalProgressDescription
            title='New members'
            progress={newMembersGoalProgress}
            goal={newMembersGoal}
          />
        </Grid>
        <Grid
          item
          display='flex'
          flexDirection='row'
          sx={{ marginBottom: { xs: 1, sm: 0 } }}
          xs={12}
          sm={4}
          pr={1}
          alignItems='center'
        >
          <RetailerNetworkMonthlyGoalCircularProgress
            color={loyaltyTransactionsColor}
            value={calculatePercentOfNumber(
              loyaltyTransactionsGoalProgress,
              loyaltyTransactionsGoal,
            )}
          />
          <RetailerNetworkMonthlyGoalProgressDescription
            title='Loyalty Transactions'
            progress={loyaltyTransactionsGoalProgress}
            goal={loyaltyTransactionsGoal}
          />
        </Grid>

        <Grid
          item
          sx={{ marginLeft: { xs: 2, sm: 0 } }}
          xs={12}
          sm={4}
          pr={1}
          alignContent='center'
        >
          <Box>
            <Typography color='textSecondary' variant='body2'>
              Home Store Members
            </Typography>
            <Typography variant='h5'>+{separateEveryThousandByComma(homeStoreMembers)}</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box
        display='flex'
        padding={2}
        marginTop={4}
        flexDirection='row'
        sx={{
          alignItems: 'center',
          backgroundColor: narrativeBoxBgColor,
          borderRadius: 1,
        }}
      >
        {narrativeTextData.isGoalComplete ? (
          <TaskAlt color='success' />
        ) : (
          <WarningAmber color='warning' />
        )}
        <Box marginLeft={2}>
          <Typography fontWeight='bold' color='textSecondary'>
            {narrativeTextData.narrativeTextTitle}
          </Typography>
          {narrativeTextData.narrativeTextMembershipDescription}
          {narrativeTextData.narrativeTextTransactionsDescription}
        </Box>
      </Box>
    </CardContent>
  );
};
